import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { sairDosOutros } from '../../auth';
import type { Sessao } from '../../auth';
import { lerMeuAniversario, salvarMeuAniversario, trocarMinhaSenha } from '../../acesso';
import type { Eu } from '../../acesso';
import { FotoOuIniciais } from '../../design/FotoDePerfil';
import { Icone } from '../../design/Icone';
import {
  enviarMinhaFoto,
  esquecerFotosDasContas,
  prepararFotoDePerfil,
  tirarMinhaFoto,
} from '../../foto-de-perfil';
import { mensagemDeFalha } from '../equipe/Equipe';
import { UNIDADE } from '../equipe/unidade';
import '../equipe/acesso.css';
import { abrirTutorial } from '../balconista/TourDeBoasVindas';
import './conta.css';
import { propsDeSenhaQueNaoSalva } from '../../design/semSalvarSenha';

/**
 * TELA 07-D — MINHA CONTA. "Página de sistema, não página esquecida."
 *
 * Formulário com largura controlada dentro do shell full-width. Campo que o servidor não deixa a
 * própria pessoa editar aparece como LEITURA, com o motivo escrito — nada de campo desabilitado
 * sem explicação. A senha segue a política do servidor (6 caracteres, decisão do dono de 24/09).
 *
 * Preferências são deste aparelho (ficam no navegador): o som só faz sentido no celular/balcão
 * onde a venda é registrada. Só entra aqui preferência que algum código LÊ — o "modo venda
 * rápida" saiu (26/09) porque o interruptor não mudava nada.
 *
 * Foto do perfil (0050, paridade com o 1.0): a própria pessoa põe, troca ou tira. A tela recorta e
 * reduz antes de enviar; quem aceita ou recusa é o servidor.
 */

type Secao = 'dados' | 'senha' | 'preferencias' | 'sessao';
const MINIMO = 6;

export function MinhaConta({
  eu,
  sessao,
  foco,
  aoSair,
  aoMudarFoto,
}: {
  eu: Eu | null;
  sessao: Sessao;
  foco: Secao;
  aoSair: () => void;
  /** A foto mudou: o shell troca o avatar na hora, sem reler `/api/me`. */
  aoMudarFoto?: (foto: string | null) => void;
}) {
  const refs = {
    dados: useRef<HTMLDivElement>(null),
    senha: useRef<HTMLDivElement>(null),
    preferencias: useRef<HTMLDivElement>(null),
    sessao: useRef<HTMLDivElement>(null),
  };
  const [ativa, setAtiva] = useState<Secao>(foco);

  useEffect(() => {
    setAtiva(foco);
    if (foco !== 'dados') refs[foco].current?.scrollIntoView({ block: 'start' });
  }, [foco]);

  const ir = (s: Secao) => {
    setAtiva(s);
    refs[s].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const nome = eu ? eu.nomeExibicao || eu.nome : '';
  const funcao = eu?.perfis.map((p) => p.nome).join(' · ') ?? '';

  return (
    <div className="ac mc">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo mc-titulo">Minha conta</h1>
          <div className="ac-subtitulo mc-subtitulo">
            {eu ? `${eu.nome} · ${funcao} · cód ${eu.login} · ${UNIDADE}` : 'Carregando…'}
          </div>
        </div>
      </div>

      <div className="mc-grade">
        <nav className="mc-nav" aria-label="Seções da conta">
          {(
            [
              ['dados', 'Meus dados'],
              ['senha', 'Senha e acesso'],
              ['preferencias', 'Preferências'],
              ['sessao', 'Sessões'],
            ] as const
          ).map(([id, rotulo]) => (
            <button
              key={id}
              type="button"
              className={`mc-nav-item${ativa === id ? ' mc-nav-ativo' : ''}`}
              aria-current={ativa === id ? 'true' : undefined}
              onClick={() => ir(id)}
            >
              {rotulo}
            </button>
          ))}
        </nav>

        <div className="mc-coluna">
          <div className="mc-cartao" ref={refs.dados}>
            <div className="mc-cartao-titulo">Identificação</div>
            <MinhaFoto nome={nome} foto={eu?.foto ?? null} sessao={sessao} aoMudar={aoMudarFoto} />
            <div className="mc-campos">
              <Leitura
                rotulo="Nome completo"
                valor={eu?.nome ?? ''}
                motivo="Corrigido pelo administrador, na Equipe."
              />
              <Leitura
                rotulo="Código no sistema"
                valor={eu?.login ?? ''}
                mono
                motivo="Definido no cadastro. Só o administrador altera."
              />
              <Leitura rotulo="Função" valor={funcao} motivo="Alterada apenas na tela de Equipe." />
              <Leitura
                rotulo="Unidade"
                valor={UNIDADE}
                motivo="Uma unidade só: não há o que escolher."
              />
            </div>
            <MeuAniversario sessao={sessao} />
          </div>

          <div ref={refs.senha}>
            <TrocarSenha sessao={sessao} />
          </div>
        </div>

        <div className="mc-coluna">
          <div className="mc-cartao" ref={refs.preferencias}>
            <div className="mc-cartao-titulo" style={{ marginBottom: 12 }}>
              Preferências
            </div>
            <Preferencia chave="som-venda" titulo="Som ao registrar venda" padrao />
            <div className="mc-pe">Guardadas neste aparelho.</div>
            {eu?.capacidades.includes('vendas.registrar') && (
              <button type="button" className="mc-tour" onClick={abrirTutorial}>
                Ver o tutorial de novo
              </button>
            )}
          </div>

          <div className="mc-cartao" ref={refs.sessao}>
            <div className="mc-cartao-titulo" style={{ marginBottom: 12 }}>
              Sessão
            </div>
            <div className="mc-sessao">
              <div style={{ flex: 1 }}>
                <div className="mc-sessao-titulo">Este navegador</div>
                <div className="mc-sessao-texto">
                  {navegadorAtual()} · desde {horaDe(sessao.criadaEm)}
                </div>
              </div>
              <span className="ac-status ac-status-ativo" style={{ height: 21, fontSize: 11.5 }}>
                Atual
              </span>
            </div>
            <div className="mc-pe" style={{ marginTop: 11 }}>
              Trocar a senha encerra as suas outras sessões, em qualquer aparelho.
            </div>
            <SairDosOutros sessao={sessao} />
            <button type="button" className="mc-sair" onClick={aoSair}>
              Sair da conta
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Pôr, trocar ou tirar a própria foto. Sem foto, as iniciais (como sempre foi). */
function MinhaFoto({
  nome,
  foto,
  sessao,
  aoMudar,
}: {
  nome: string;
  foto: string | null;
  sessao: Sessao;
  aoMudar: ((foto: string | null) => void) | undefined;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);

  const mudou = (nova: string | null, texto: string) => {
    aoMudar?.(nova);
    esquecerFotosDasContas();
    setMsg({ ok: true, texto });
  };

  const escolher = async (arquivo: File | undefined) => {
    if (!arquivo || enviando) return;
    setEnviando(true);
    setMsg(null);
    try {
      const r = await enviarMinhaFoto(sessao, await prepararFotoDePerfil(arquivo));
      if (r.ok) mudou(r.dados.foto, 'Foto atualizada. A equipe vê ela no lugar das iniciais.');
      else setMsg({ ok: false, texto: r.mensagem });
    } catch (e) {
      setMsg({ ok: false, texto: e instanceof Error ? e.message : 'Não deu para usar essa foto.' });
    } finally {
      setEnviando(false);
      // a mesma foto pode ser escolhida de novo
      if (entrada.current) entrada.current.value = '';
    }
  };

  const tirar = async () => {
    setEnviando(true);
    setMsg(null);
    const r = await tirarMinhaFoto(sessao);
    setEnviando(false);
    if (r.ok) mudou(null, 'Foto tirada. Voltaram as iniciais.');
    else setMsg({ ok: false, texto: r.mensagem });
  };

  return (
    <div className="mc-foto-bloco">
      <div className="mc-foto">
        <span className="mc-avatar" aria-hidden="true">
          {nome ? <FotoOuIniciais foto={foto} nome={nome} /> : ''}
        </span>
        <div className="mc-foto-miolo">
          <div className="mc-foto-titulo">Foto do perfil</div>
          <div className="mc-foto-texto">
            {foto
              ? 'Aparece para a equipe, só dentro do sistema.'
              : 'Sem foto, usamos as iniciais.'}
          </div>
          <div className="mc-foto-acoes">
            <input
              ref={entrada}
              type="file"
              accept="image/*"
              hidden
              aria-label="Escolher foto do perfil"
              onChange={(e) => void escolher(e.target.files?.[0])}
            />
            <button
              type="button"
              className="mc-outros-botao"
              disabled={enviando}
              onClick={() => entrada.current?.click()}
            >
              {enviando ? 'Enviando…' : foto ? 'Trocar foto' : 'Enviar foto'}
            </button>
            {foto && (
              <button
                type="button"
                className="mc-outros-botao"
                disabled={enviando}
                onClick={() => void tirar()}
              >
                Tirar foto
              </button>
            )}
          </div>
        </div>
      </div>
      {msg && (
        <div className={msg.ok ? 'mc-ok' : 'mc-erro'} role={msg.ok ? 'status' : 'alert'}>
          <Icone nome={msg.ok ? 'check' : 'warn'} tamanho={14} traco={2} />
          {msg.texto}
        </div>
      )}
    </div>
  );
}

function Leitura({
  rotulo,
  valor,
  motivo,
  mono,
}: {
  rotulo: string;
  valor: string;
  motivo: string;
  mono?: boolean;
}) {
  return (
    <div>
      <span className="mc-rotulo">{rotulo}</span>
      <div className={`mc-leitura${mono ? ' mc-leitura-mono' : ''}`}>{valor || ' '}</div>
      <div className="mc-motivo">{motivo}</div>
    </div>
  );
}

function TrocarSenha({ sessao }: { sessao: Sessao }) {
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [repetida, setRepetida] = useState('');
  const [ver, setVer] = useState({ atual: false, nova: false });
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<
    | { ok: true; texto: string }
    | { ok: false; texto: string; campo: 'atual' | 'nova' | null }
    | null
  >(null);

  const naoConferem = repetida.length > 0 && nova !== repetida;
  const curta = nova.length > 0 && [...nova].length < MINIMO;

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    if (!atual) return setResultado({ ok: false, campo: 'atual', texto: 'Informe a senha atual.' });
    if ([...nova].length < MINIMO)
      return setResultado({
        ok: false,
        campo: 'nova',
        texto: `A nova senha precisa de pelo menos ${String(MINIMO)} caracteres.`,
      });
    if (nova !== repetida)
      return setResultado({ ok: false, campo: 'nova', texto: 'As senhas não conferem.' });
    setEnviando(true);
    setResultado(null);
    const r = await trocarMinhaSenha(sessao, atual, nova);
    setEnviando(false);
    setAtual('');
    if (r.ok) {
      setNova('');
      setRepetida('');
      const n = r.dados.outrasSessoesEncerradas;
      setResultado({
        ok: true,
        texto:
          n === 0
            ? 'Senha trocada. Você continua conectado aqui.'
            : `Senha trocada. ${String(n)} ${n === 1 ? 'outra sessão foi encerrada' : 'outras sessões foram encerradas'}; você continua conectado aqui.`,
      });
      return;
    }
    const texto = mensagemDeFalha(r.falha);
    setResultado({
      ok: false,
      campo: texto.includes('senha atual') ? 'atual' : texto.includes('nova senha') ? 'nova' : null,
      texto,
    });
  }

  const erroEm = (c: 'atual' | 'nova') => resultado && !resultado.ok && resultado.campo === c;

  return (
    <form className="mc-cartao" autoComplete="off" onSubmit={(e) => void salvar(e)} noValidate>
      <div className="mc-cartao-titulo" style={{ marginBottom: 4 }}>
        Trocar senha
      </div>
      <div className="mc-cartao-sub">
        Pelo menos {MINIMO} caracteres — uma frase fica fácil de lembrar.
      </div>
      <div className="mc-campos">
        <CampoSenha
          id="mc-atual"
          rotulo="Senha atual"
          valor={atual}
          aoMudar={setAtual}
          visivel={ver.atual}
          aoVer={() => setVer((v) => ({ ...v, atual: !v.atual }))}
          erro={erroEm('atual') ?? false}
          desabilitado={enviando}
        />
        <div className="mc-vazio-desktop" />
        <CampoSenha
          id="mc-nova"
          rotulo="Nova senha"
          valor={nova}
          aoMudar={setNova}
          visivel={ver.nova}
          aoVer={() => setVer((v) => ({ ...v, nova: !v.nova }))}
          erro={curta || (erroEm('nova') ?? false)}
          desabilitado={enviando}
          aviso={curta ? `Faltam ${String(MINIMO - [...nova].length)} caracteres` : null}
        />
        <CampoSenha
          id="mc-repetida"
          rotulo="Confirmar nova senha"
          valor={repetida}
          aoMudar={setRepetida}
          visivel={ver.nova}
          erro={naoConferem}
          desabilitado={enviando}
          aviso={naoConferem ? 'As senhas não conferem' : null}
        />
      </div>
      {resultado && (
        <div
          className={resultado.ok ? 'mc-ok' : 'mc-erro'}
          role={resultado.ok ? 'status' : 'alert'}
        >
          <Icone nome={resultado.ok ? 'check' : 'warn'} tamanho={14} traco={2} />
          {resultado.texto}
        </div>
      )}
      <button type="submit" className="mc-salvar" disabled={enviando}>
        {enviando ? 'Salvando…' : 'Salvar nova senha'}
      </button>
    </form>
  );
}

function CampoSenha({
  id,
  rotulo,
  valor,
  aoMudar,
  visivel,
  aoVer,
  erro,
  aviso,
  desabilitado,
}: {
  id: string;
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  visivel: boolean;
  aoVer?: () => void;
  erro: boolean;
  aviso?: string | null;
  desabilitado: boolean;
}) {
  return (
    <label htmlFor={id} style={{ display: 'block' }}>
      <span className="mc-rotulo">{rotulo}</span>
      <div className="mc-senha">
        <input
          id={id}
          {...propsDeSenhaQueNaoSalva(visivel)}
          value={valor}
          onChange={(e) => aoMudar(e.target.value)}
          disabled={desabilitado}
          className={`mc-entrada${erro ? ' mc-entrada-erro' : ''}`}
          aria-invalid={erro}
        />
        {aoVer && (
          <button
            type="button"
            className="mc-olho"
            onClick={aoVer}
            aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
            aria-pressed={visivel}
          >
            <Icone nome="eye" tamanho={16} traco={1.6} />
          </button>
        )}
      </div>
      {aviso && (
        <span className="mc-aviso">
          <Icone nome="warn" tamanho={13} traco={1.8} />
          {aviso}
        </span>
      )}
    </label>
  );
}

function Preferencia({
  chave,
  titulo,
  texto,
  padrao,
}: {
  chave: string;
  titulo: string;
  texto?: string;
  padrao: boolean;
}) {
  const nomeNoAparelho = `inovapay2.pref.${chave}`;
  const [ligada, setLigada] = useState(() => {
    try {
      const v = window.localStorage.getItem(nomeNoAparelho);
      return v === null ? padrao : v === '1';
    } catch {
      return padrao;
    }
  });
  return (
    <div className="mc-pref">
      <div style={{ flex: 1 }}>
        <div className="mc-pref-titulo">{titulo}</div>
        {texto && <div className="mc-pref-texto">{texto}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={ligada}
        aria-label={titulo}
        className={`mc-chave${ligada ? ' mc-chave-ligada' : ''}`}
        onClick={() => {
          setLigada((v) => {
            try {
              window.localStorage.setItem(nomeNoAparelho, v ? '0' : '1');
            } catch {
              // navegador sem armazenamento: a preferência vale só nesta visita
            }
            return !v;
          });
        }}
      >
        <span />
      </button>
    </div>
  );
}

function navegadorAtual(): string {
  const ua = navigator.userAgent;
  const nav = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Navegador';
  const so = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad/.test(ua)
      ? 'iPhone'
      : /Windows/.test(ua)
        ? 'Windows'
        : /Mac OS/.test(ua)
          ? 'Mac'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';
  return so ? `${nav} · ${so}` : nav;
}

function horaDe(iso: string): string {
  const d = new Date(iso);
  const hoje = new Date();
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === hoje.toDateString()
    ? hora
    : `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${hora}`;
}

/** Esqueceu o InovaPay aberto em outro celular ou computador? Esta sessão fica; as outras caem. */
function SairDosOutros({ sessao }: { sessao: Sessao }) {
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const sairAgora = async () => {
    setEnviando(true);
    setMsg(null);
    const n = await sairDosOutros(sessao);
    setEnviando(false);
    setMsg(
      n === null
        ? 'Não deu para confirmar. Verifique a conexão e tente de novo.'
        : n === 0
          ? 'Nenhum outro aparelho estava conectado.'
          : `${String(n)} ${n === 1 ? 'aparelho desconectado' : 'aparelhos desconectados'}. Só este continua conectado.`,
    );
  };
  return (
    <div className="mc-outros">
      <button
        type="button"
        className="mc-outros-botao"
        disabled={enviando}
        onClick={() => void sairAgora()}
      >
        {enviando ? 'Desconectando…' : 'Sair dos outros aparelhos'}
      </button>
      {msg && (
        <div className="mc-pe" role="status">
          {msg}
        </div>
      )}
    </div>
  );
}

const NOMES_DOS_MESES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/** Aniversário (só dia e mês): aparece no Início de todos no dia. Opcional. */
function MeuAniversario({ sessao }: { sessao: Sessao }) {
  const [dia, setDia] = useState('');
  const [mes, setMes] = useState('');
  const [salvo, setSalvo] = useState<string>('');
  const [msg, setMsg] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    void lerMeuAniversario().then((r) => {
      if (!r.ok || !r.dados.aniversario) return;
      setDia(String(r.dados.aniversario.dia));
      setMes(String(r.dados.aniversario.mes));
      setSalvo(`${String(r.dados.aniversario.dia)}/${String(r.dados.aniversario.mes)}`);
    });
  }, []);
  const atual = dia && mes ? `${dia}/${mes}` : '';
  const salvar = async (limpar = false) => {
    setEnviando(true);
    setMsg(null);
    const r = await salvarMeuAniversario(
      sessao,
      limpar || !dia || !mes ? null : { dia: Number(dia), mes: Number(mes) },
    );
    setEnviando(false);
    if (!r.ok) return setMsg(mensagemDeFalha(r.falha));
    if (limpar) {
      setDia('');
      setMes('');
    }
    setSalvo(limpar ? '' : atual);
    setMsg(limpar ? 'Aniversário apagado.' : 'Salvo. No dia, ele aparece no Início da equipe.');
  };
  return (
    <div className="mc-aniversario">
      <div className="mc-campo-rotulo">Aniversário (opcional)</div>
      <div className="mc-aniversario-linha">
        <select value={dia} onChange={(e) => setDia(e.target.value)} aria-label="Dia">
          <option value="">Dia</option>
          {Array.from({ length: 31 }, (_, i) => (
            <option key={i + 1} value={String(i + 1)}>
              {i + 1}
            </option>
          ))}
        </select>
        <select value={mes} onChange={(e) => setMes(e.target.value)} aria-label="Mês">
          <option value="">Mês</option>
          {NOMES_DOS_MESES.map((m, i) => (
            <option key={m} value={String(i + 1)}>
              {m}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="mc-outros-botao"
          disabled={enviando || !dia || !mes || atual === salvo}
          onClick={() => void salvar()}
        >
          Salvar
        </button>
        {salvo && (
          <button
            type="button"
            className="mc-outros-botao"
            disabled={enviando}
            onClick={() => void salvar(true)}
          >
            Apagar
          </button>
        )}
      </div>
      <div className="mc-pe">Só dia e mês — sem ano.</div>
      {msg && (
        <div className="mc-pe" role="status">
          {msg}
        </div>
      )}
    </div>
  );
}
